import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useAuth, authenticateUser, authmeUser, authenticateSSO, getAddInOrigin, UnicliTheme, LoginForm, WelcomeScreen } from "common";
import UnicliChat from "./UnicliChat";
import { loadTheme } from "@fluentui/react";
import { jwtDecode } from "jwt-decode";

loadTheme(UnicliTheme);

const getWordDocument = () => Office.context?.document ?? null;

const App = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [authInProgress, setAuthInProgress] = useState(true);
  const [loginError, setLoginError] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [officeType, setOfficeType] = useState("WORD");
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState(null);

  const { authState, login, logout } = useAuth();

  useEffect(() => {
    const initApp = async () => {
      setAuthInProgress(true);

      setCurrentItem(getWordDocument());
      setOfficeType("WORD");

      // (a) Check first-time user
      const storedFirstTimeViewed = localStorage.getItem("storedFirstTimeViewed");
      setIsFirstTime(!storedFirstTimeViewed);

      // (b) Try stored token
      let didLogin = false;
      const storedAuth = localStorage.getItem("authState");
      if (storedAuth) {
        try {
          const { authToken } = JSON.parse(storedAuth);
          const response = await authmeUser(authToken);
          if (response) {
            setUser(response);
            didLogin = true;
          } else {
            logout();
          }
        } catch (error) {
          console.error("Token validation failed:", error);
          logout();
        }
      }

      // If no valid stored token, try SSO
      if (!didLogin && Office?.context?.auth) {
        try {
          const officeToken = await OfficeRuntime.auth.getAccessToken({
            allowSignInPrompt: true,
            allowConsentPrompt: true,
          });
          const officeTokenDecoded = jwtDecode(officeToken);
          console.log("officeTokenDecoded", officeTokenDecoded);

          const { authToken, user } = await authenticateSSO(
            officeTokenDecoded.preferred_username,
            officeToken,
            getAddInOrigin()
          );
          if (authToken && user) {
            login(authToken, user);
            setUser(user);
            didLogin = true;
          }
        } catch (error) {
          console.error("SSO login failed:", error);
        }
      }

      fetch("/version.json")
        .then((response) => response.json())
        .then((data) => console.log(`Add-in Version: ${data.version}`))
        .catch((error) => console.error("Error fetching version:", error));

      setAuthInProgress(false);
      setIsReady(true);
    };

    Office.onReady(() => {
      initApp();
    });
  }, [login, logout]);

  const handleLogin = async (email, password) => {
    try {
      const { authToken, user } = await authenticateUser(email, password);
      console.log("authToken", authToken);
      console.log("user", user);
      login(authToken, user);
      // Navigate to the dashboard or main part of the app
    } catch (error) {
      console.log("Login failed: Invalid email or password");
      setLoginError(true);
    }
  };
  const handleWelcomeScreenClick = () => {
    setIsFirstTime(false);
    localStorage.setItem("storedFirstTimeViewed", true);
  };
  if (authInProgress) {
    return <div>Authenticating...</div>;
  }
  if (!isReady) {
    return <div>Loading...</div>;
  }

  if (!authState) {
    return (
      <div>
        {isFirstTime ? (
          <WelcomeScreen onGettingStartedClick={handleWelcomeScreenClick} addinType="WORD" />
        ) : (
          <LoginForm onLogin={handleLogin} loginError={loginError} />
        )}
      </div>
    );
  }

  return <UnicliChat currentItem={currentItem} officeType={officeType} currentUser={user} />;
};

App.propTypes = {
  title: PropTypes.string,
};

export default App;
